import React from 'react';
import { Link } from 'react-router-dom';
import { formatUsd } from '../contexts/CurrencyContext';
import { useLanguage } from '../contexts/LanguageContext';
import './PremiumBenefitsPanel.css';

const intelligenceBenefits = [
  { title: 'Product performance', text: 'Historical impressions, clicks, CTR and average interaction time.' },
  { title: 'Real search demand', text: 'Related searches and demand across subcategory, category and sector.' },
  { title: 'Regional price intelligence', text: 'Similar-product counts and average prices by city, state, country and globally.' },
  { title: 'No sales commission', text: 'Eulua does not add a marketplace commission to your product prices or completed orders.' },
];

const PremiumBenefitsPanel = ({ variant = 'home', onSellerAction }) => {
  const detailed = variant === 'sell';
  const { language, t } = useLanguage();
  return (
    <section className={`premium-benefits premium-benefits--${variant}`} aria-labelledby={`premium-benefits-${variant}-title`}>
      <div className="premium-benefits__container">
        <header className="premium-benefits__heading">
          <div>
            {!detailed && <span>{t('Eulua Premium Account')}</span>}
            <h2 id={`premium-benefits-${variant}-title`}>
              {t(detailed ? 'Start free. Upgrade when data can help you grow.' : 'Turn marketplace activity into better business decisions.')}
            </h2>
          </div>
        </header>

        <div className="premium-benefits__intelligence">
          {intelligenceBenefits.map(({ title, text }, index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div><h3>{t(title)}</h3><p>{t(text)}</p></div>
            </article>
          ))}
        </div>

        <div className="premium-benefits__plans">
          <article className="premium-benefits__plan">
            <div className="premium-benefits__plan-name"><strong>{t('Starter')}</strong><small>{t('Included')}</small></div>
            <h3>{formatUsd(0)} <span>{t('/ month')}</span></h3>
            <ul>
              <li>{t('Seller account and unlimited catalog')}</li>
              <li>{t('Product and profile advertising')}</li>
              <li>{language === 'es' ? 'Sin comisión de venta' : 'No sales commission'}</li>
            </ul>
          </article>

          <article className="premium-benefits__plan premium-benefits__plan--lite">
            <div className="premium-benefits__plan-name"><strong>{t('Lite')}</strong><small>{t('Available')}</small></div>
            <h3>{formatUsd(29)} <span>{t('/ month')}</span></h3>
            <ul>
              <li>{language === 'es' ? 'Solicitudes de cotización ilimitadas' : 'Unlimited quote requests'}</li>
              <li>{language === 'es' ? `${formatUsd(19)} de crédito publicitario mensual` : `${formatUsd(19)} monthly advertising credit`}</li>
              <li>{t('Impressions and clicks from the last 30 days')}</li>
              <li>{t('Realtime product activity')}</li>
              <li>{t('Inventory Excel export')}</li>
              <li>{language === 'es' ? 'Sin comisión de venta' : 'No sales commission'}</li>
            </ul>
          </article>

          <article className="premium-benefits__plan premium-benefits__plan--growth">
            <div className="premium-benefits__plan-name"><strong>{t('Growth')}</strong><small>{t('Available')}</small></div>
            <h3>{formatUsd(99)} <span>{t('/ month')}</span></h3>
            <ul>
              <li>{t('Complete analytics for every product')}</li>
              <li>{t('Price and keyword intelligence')}</li>
              <li>{t('Inventory Excel export')}</li>
              <li>{language === 'es' ? 'Sin comisión de venta' : 'No sales commission'}</li>
            </ul>
          </article>

          <article className="premium-benefits__plan premium-benefits__plan--enterprise">
            <div className="premium-benefits__enterprise-ribbon">{t('Built for teams')}</div>
            <div className="premium-benefits__plan-name"><strong>{t('Enterprise')}</strong><small>{t('Available')}</small></div>
            <h3>{formatUsd(299)} <span>{t('/ month')}</span></h3>
            <p className="premium-benefits__enterprise-copy">{t('One company account, shared securely by the people who manage your operation.')}</p>
            <ul className="premium-benefits__enterprise-list">
              <li>{t('Multiple users on the same company account')}</li>
              <li>{language === 'es' ? 'Sin comisión de venta' : 'No sales commission'}</li>
              <li>{t('Stock movement Excel history')}</li>
              <li>{t('API integration')}</li>
              <li>{t('Priority support')}</li>
            </ul>
            <span className="premium-benefits__enterprise-status">{t('AVAILABLE NOW')}</span>
          </article>
        </div>

        <footer className="premium-benefits__footer">
          <div><span><strong>{t('No commitment to upgrade.')}</strong> {t('Every approved seller begins on Starter.')}</span></div>
          {onSellerAction ? (
            <button type="button" onClick={onSellerAction}>{t('Start with Starter')}</button>
          ) : (
            <Link to="/sell">{t('Explore selling on Eulua')}</Link>
          )}
        </footer>
      </div>
    </section>
  );
};

export default PremiumBenefitsPanel;
